import React, { useState, useEffect } from 'react';
import { Shield, Plus, Trash2, Eye, EyeOff, Save, Loader2, AlertTriangle, LogOut } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAGE_LABELS = {
  books: { label: 'Books', desc: 'Books hub and book pages' },
  articles: { label: 'Articles', desc: 'Published articles and insights' },
  speaking: { label: 'Speaking', desc: 'Speaking engagements page' },
  podcast: { label: 'Podcast', desc: 'Podcast episodes page' },
  social: { label: 'Social', desc: 'Social hub and posts' },
  about: { label: 'About', desc: 'About page' },
  courses: { label: 'Courses', desc: 'Online courses' },
  organisations: { label: 'Organisations', desc: 'Organisations and workshops' },
  contact: { label: 'Contact', desc: 'Contact form' },
  pillars: { label: 'Pillars', desc: 'Four pillars framework page' },
  cfq: { label: 'CFQ Assessment', desc: 'Cultural Fluency Quotient tool' },
  'for-speakers': { label: 'For Speakers', desc: 'Speaker permission and QR info' },
};

const AdminSettings = () => {
  const { logout, token } = useAuth();
  const [whitelist, setWhitelist] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [toggles, setToggles] = useState({});
  const [togglesLoading, setTogglesLoading] = useState(true);
  const [togglesSaving, setTogglesSaving] = useState(false);
  const [togglesSaved, setTogglesSaved] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [revokeConfirm, setRevokeConfirm] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchWhitelist = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/settings/whitelist`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const data = await res.json(); setWhitelist(data.whitelist); }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchToggles = async () => {
    setTogglesLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/site/page-toggles`);
      if (res.ok) { const data = await res.json(); setToggles(data.toggles || {}); }
    } catch (e) { console.error(e); }
    setTogglesLoading(false);
  };

  useEffect(() => { fetchWhitelist(); fetchToggles(); }, []);

  const handleAdd = async () => {
    if (!newEmail) return;
    try {
      await fetch(`${API_URL}/api/cms/settings/whitelist?email=${encodeURIComponent(newEmail)}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      setNewEmail('');
      fetchWhitelist();
    } catch (e) { console.error(e); }
  };

  const handleRemove = async (id) => {
    if (!window.confirm('Remove this admin email?')) return;
    await fetch(`${API_URL}/api/cms/settings/whitelist/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchWhitelist();
  };

  const handleToggle = (key) => {
    setToggles(prev => ({ ...prev, [key]: !prev[key] }));
    setTogglesSaved(false);
  };

  const saveToggles = async () => {
    setTogglesSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/site/page-toggles`, {
        method: 'PUT', headers,
        body: JSON.stringify({ toggles }),
      });
      if (res.ok) {
        setTogglesSaved(true);
        setTimeout(() => setTogglesSaved(false), 2000);
      }
    } catch (e) { console.error(e); }
    setTogglesSaving(false);
  };

  const revokeAllSessions = async () => {
    if (!revokeConfirm) { setRevokeConfirm(true); return; }
    setRevoking(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/revoke-sessions`, { method: 'POST', headers });
      if (res.ok) {
        // Sign out this device too — the current token is now invalidated
        logout();
      }
    } catch (e) { console.error(e); }
    setRevoking(false);
    setRevokeConfirm(false);
  };

  return (
    <div data-testid="admin-settings">
      <div className="mb-8">
        <h1 className="font-serif text-2xl text-cream font-semibold">Settings</h1>
        <p className="text-cream/30 text-sm mt-1">Page visibility, admin whitelist & site configuration</p>
      </div>

      {/* Page Visibility Toggles */}
      <div className="bg-navy border border-gold/[0.06] rounded-xl p-6 mb-6" data-testid="page-toggles-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Eye size={16} className="text-gold/60" />
            <h2 className="text-cream text-sm font-semibold">Page Visibility</h2>
          </div>
          <button
            onClick={saveToggles}
            disabled={togglesSaving}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-gold text-navy text-xs font-medium rounded-lg hover:bg-gold/90 disabled:opacity-50"
            data-testid="save-toggles-btn"
          >
            {togglesSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {togglesSaved ? 'Saved' : 'Save changes'}
          </button>
        </div>
        <p className="text-cream/25 text-xs mb-5">Switch pages on or off. Disabled pages are hidden from the navigation and visitors are redirected to the homepage.</p>

        {togglesLoading ? (
          <div className="py-8 text-center text-cream/20 text-sm">Loading...</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(PAGE_LABELS).map(([key, { label, desc }]) => {
              const enabled = toggles[key] !== false;
              return (
                <button
                  key={key}
                  onClick={() => handleToggle(key)}
                  className={`flex items-center gap-3 p-4 rounded-lg border text-left transition-all ${
                    enabled
                      ? 'border-emerald-500/20 bg-emerald-500/[0.04] hover:border-emerald-500/30'
                      : 'border-cream/[0.06] bg-cream/[0.01] hover:border-cream/10'
                  }`}
                  data-testid={`toggle-${key}`}
                >
                  <div className={`w-10 h-6 rounded-full flex items-center transition-colors ${enabled ? 'bg-emerald-500 justify-end' : 'bg-cream/10 justify-start'}`}>
                    <div className={`w-5 h-5 rounded-full bg-white shadow mx-0.5 transition-transform`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${enabled ? 'text-cream' : 'text-cream/30'}`}>{label}</p>
                    <p className="text-cream/15 text-[10px] truncate">{desc}</p>
                  </div>
                  {enabled ? <Eye size={14} className="text-emerald-400/50 flex-shrink-0" /> : <EyeOff size={14} className="text-cream/15 flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Admin Whitelist */}
      <div className="bg-navy border border-gold/[0.06] rounded-xl p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Shield size={16} className="text-gold/60" />
          <h2 className="text-cream text-sm font-semibold">Admin Email Whitelist</h2>
        </div>
        <p className="text-cream/25 text-xs mb-4">Only whitelisted emails can access the admin panel.</p>

        <div className="flex gap-2 mb-4">
          <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="admin@example.com" className="flex-1 bg-navy-deep border border-cream/10 rounded-lg px-3 py-2 text-sm text-cream placeholder-cream/20 focus:outline-none focus:ring-1 focus:ring-gold/30" data-testid="whitelist-email-input" />
          <button onClick={handleAdd} className="px-4 py-2 bg-gold text-navy text-sm font-semibold rounded-lg hover:bg-gold-light" data-testid="whitelist-add-btn">
            <Plus size={14} className="inline mr-1" /> Add
          </button>
        </div>

        {loading ? <div className="py-8 text-center text-cream/20 text-sm">Loading...</div> : (
          <div className="space-y-2">
            {whitelist.map((entry) => (
              <div key={entry.id} className="flex items-center justify-between bg-navy-deep rounded-lg px-4 py-3">
                <div>
                  <p className="text-cream/60 text-sm">{entry.email}</p>
                  <p className="text-cream/15 text-[10px]">Added by {entry.added_by} on {new Date(entry.added_at).toLocaleDateString('en-GB')}</p>
                </div>
                <button onClick={() => handleRemove(entry.id)} className="p-1.5 text-cream/20 hover:text-red-400 transition-colors" data-testid={`whitelist-remove-${entry.email}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Security — Session Revocation */}
      <div className="bg-navy border border-gold/[0.06] rounded-xl p-6" data-testid="security-section">
        <div className="flex items-center gap-2 mb-4">
          <Shield size={16} className="text-gold/60" />
          <h2 className="text-cream text-sm font-semibold">Security</h2>
        </div>
        <p className="text-cream/25 text-xs mb-5">
          Signing out all devices immediately invalidates every active session token.
          You will need to log in again on this device after confirming.
        </p>
        <div className="flex items-center gap-3">
          <button
            onClick={revokeAllSessions}
            disabled={revoking}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
              revokeConfirm
                ? 'bg-red-500 text-white hover:bg-red-600'
                : 'bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20'
            } disabled:opacity-50`}
            data-testid="revoke-sessions-btn"
          >
            {revoking ? <Loader2 size={14} className="animate-spin" /> : <LogOut size={14} />}
            {revokeConfirm ? 'Confirm — Sign out all devices' : 'Sign out all devices'}
          </button>
          {revokeConfirm && (
            <button
              onClick={() => setRevokeConfirm(false)}
              className="text-cream/30 hover:text-cream/60 text-sm transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
        {revokeConfirm && (
          <p className="text-amber-400/60 text-xs mt-3 flex items-center gap-1.5">
            <AlertTriangle size={11} />
            This will immediately log you out of this device too.
          </p>
        )}
      </div>
    </div>
  );
};

export default AdminSettings;
